import moment from 'moment'
import * as React from 'react'
import Helmet from 'react-helmet'

import Footer from 'main/components/common/Footer'
import Header, { ISearchProps as IHeaderSearchProps } from 'main/components/common/Header'
import { useWhitelabel } from 'main/framework'
import useAppFeatureFlags from 'main/hooks/useAppFeatureFlags'
import { AssetName } from 'main/util/asset'
import { attributes as faviconAttributes, Size as FaviconSize } from 'main/util/favicon'
import { IPageAlert } from 'main/util/page'

import BTSBanner from '../BTSBanner'
import { Container, ContentContainer, FooterContainer } from './Page.styles'
import { PageContext } from './PageContext'

interface IHeaderParams {
  alwaysShowSearchForm?: boolean
  alwaysSticky?: boolean
  currentSearch?: IHeaderSearchProps
  customRight?: React.ReactElement
  onlyShowLogo?: boolean
  disableLogoLink?: boolean
  disableAuthModal?: boolean
}

interface IProps {
  children: React.ReactNode
  headerBottomBar?: React.ReactNode
  title?: string
  description?: string
  schema?: {} | Array<{}>
  metatags?: Array<{}>
  hideFooter?: boolean
  hideHeader?: boolean
  headerParams?: IHeaderParams
  urlPath?: string
}

export default function Page(props: IProps) {
  const {
    children,
    headerBottomBar,
    title,
    description,
    schema,
    metatags,
    urlPath,
    headerParams,
    hideFooter,
    hideHeader,
  } = props

  const lego = window.lego

  const [alert, setAlert] = React.useState<IPageAlert | null>(null)

  const isBannerActive = !moment().isAfter('2019-09-15')
  const whitelabel = useWhitelabel()
  const favicon = whitelabel.getAsset(AssetName.FAVICON)
  const externalOnboardingEnabled =
    useAppFeatureFlags().appFeatureFlagEnabled('external_onboarding')

  const showFooter = !hideFooter && whitelabel.evaluateToTrueExceptIfFalse(AssetName.FOOTER)

  return (
    <PageContext.Provider value={{ setAlert }}>
      <Helmet>
        <link {...faviconAttributes(FaviconSize.LARGE, favicon)} />
        <link {...faviconAttributes(FaviconSize.SMALL, favicon)} />
        {title && <title>{title}</title>}
        {description && <meta name="description" content={description} />}
        {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
        {metatags && metatags.map(metatag => <meta {...metatag} key={JSON.stringify(metatag)} />)}
        {urlPath && <link rel="canonical" href={`${lego.config.ASSET_DOMAIN}${urlPath}`} />}
      </Helmet>
      <Container>
        {isBannerActive && <BTSBanner />}
        {!hideHeader && (
          <Header
            customRight={
              headerParams && headerParams.customRight ? headerParams.customRight : undefined
            }
            bottomBar={headerBottomBar}
            isBannerActive={isBannerActive}
            pageAlert={alert}
            onDismissPageAlert={() => setAlert(null)}
            externalOnboardingEnabled={externalOnboardingEnabled}
            {...headerParams}
          />
        )}

        <ContentContainer>{children}</ContentContainer>

        {showFooter && (
          <FooterContainer>
            <Footer />
          </FooterContainer>
        )}
      </Container>
    </PageContext.Provider>
  )
}
